import React from 'react';
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, useMediaQuery, useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import WhatsAppFlowPreview from './WhatsAppFlowPreview';

/**
 * Meta's own rendering of the flow, the same dialog in both builders.
 *
 * The preview is an iframe that fills whatever height it is given, so the dialog has to give it
 * one: a fixed paper height, a content area that is allowed to shrink (flex: 1, minHeight: 0) and
 * never scrolls itself. The assistant's copy had no height at all and squeezed the frame into a
 * strip; the canvas's copy had a height but a content area that could not shrink, so the whole
 * dialog scrolled around the frame's own scrolling (2026-09-28).
 *
 * On a phone it is full screen, with Close as an icon in the title row. The desktop dialog was
 * 96vh tall, and a phone's vh counts the space under the browser's own toolbar: the bottom of the
 * frame, where Meta puts its cookie buttons and the flow its Continue button, sat behind that
 * toolbar where nothing could tap it, and the frame had nothing left to scroll (2026-10-10).
 */
const WhatsAppPreviewDialog = ({ open, onClose, flowId }) => {
  const theme = useTheme();
  const isTouch = useMediaQuery('(hover: none) and (pointer: coarse)');
  const phone = useMediaQuery(theme.breakpoints.down('md')) || isTouch;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={phone}
      PaperProps={{
        sx: phone
          ? {
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            // The visible height, not the layout height, where the browser supports it.
            height: '100dvh',
            pb: 'env(safe-area-inset-bottom)',
          }
          : {
            width: 860,
            maxWidth: '96vw',
            height: 'min(1100px, 96vh)',
            maxHeight: '96vh',
            m: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          },
      }}
      data-testid="whatsapp-preview-dialog"
    >
      <DialogTitle sx={{ pb: 1, display: 'flex', alignItems: 'center', gap: 1, ...(phone && { py: 1, pr: 1 }) }}>
        <Box component="span" sx={{ flex: 1 }}>WhatsApp Preview</Box>
        {phone && (
          <IconButton onClick={onClose} aria-label="Close" edge="end">
            <CloseIcon />
          </IconButton>
        )}
      </DialogTitle>
      <DialogContent
        dividers
        sx={{ p: 0, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
      >
        {open && <WhatsAppFlowPreview flowDbId={flowId} compact={phone} />}
      </DialogContent>
      {!phone && (
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default WhatsAppPreviewDialog;
